import React, { useState } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import {
  CreateEdgeAppliance,
  EdgeAppliance,
  EdgeFactoryImage,
  useCreateEdgeApplianceMutation,
  useDeleteEdgeApplianceMutation,
  useGetEdgeAppliancesQuery,
  useIssueEdgeAppliancePackageMutation,
  useLazyGetLatestFactoryImageQuery,
} from "../../../redux/servers/serverAPI";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";
import { apiErrorMessage } from "../../../utils/apiErrorMessage";

const compatible = "liftnet-edge-amd64-v1";
type FactoryDownload = "image" | "tools";
// The image and its standalone operator tools zip come from one latest-image response.
const factoryDownload: Record<
  FactoryDownload,
  {
    label: string;
    success: string;
    pick: (image: EdgeFactoryImage) => { url?: string; sha256?: string };
  }
> = {
  image: {
    label: "edgeInventory.downloadImage",
    success: "edgeInventory.downloadImageSuccess",
    pick: (image) => ({ url: image.download_url, sha256: image.sha256 }),
  },
  tools: {
    label: "edgeInventory.downloadTools",
    success: "edgeInventory.downloadToolsSuccess",
    pick: (image) => ({ url: image.tools_download_url, sha256: image.tools_sha256 }),
  },
};
const hardwareChecks = ["architecture_x86_64", "uefi", "secure_boot", "tpm2", "network_interface"];
// Safari can cancel a download whose object URL is revoked in the same task as
// the click, so the URL is kept alive a little longer (FileSaver.js waits 40 s).
const OBJECT_URL_LIFETIME_MS = 40_000;
const stateColor: Record<EdgeAppliance["state"], string> = {
  ready: "default",
  package_issued: "blue",
  installed: "gold",
  activated: "green",
  retired: "red",
};

interface DiscoveredDisk {
  device: string;
  bytes?: number;
  model?: string;
  serial?: string;
  by_id: string[];
}

interface HardwareDiscovery {
  schema: number;
  compatible: string;
  chassis_serial?: string | null;
  checks: Record<string, boolean>;
  disks: DiscoveredDisk[];
  error?: string;
}

interface DiskOption {
  value: string;
  label: string;
}

function preferredDiskID(ids: string[]): string {
  const rank = (id: string) => {
    if (id.startsWith("wwn-")) return 0;
    if (id.startsWith("nvme-eui.") || id.startsWith("nvme-uuid.")) return 1;
    if (id.startsWith("ata-")) return 2;
    if (id.startsWith("scsi-")) return 3;
    return 4;
  };
  return (
    [...ids].sort((left, right) => rank(left) - rank(right) || left.localeCompare(right))[0] || ""
  );
}

function diskOptions(disks: DiscoveredDisk[]): DiskOption[] {
  return disks.flatMap((disk) => {
    const gib = disk.bytes ? ` · ${(disk.bytes / 1024 ** 3).toFixed(0)} GiB` : "";
    const identity = [disk.model, disk.serial].filter(Boolean).join(" · ");
    return disk.by_id.map((id) => ({
      value: id,
      label: `${identity || disk.device}${gib} · ${id}`,
    }));
  });
}

function downloadName(contentDisposition: string, assetTag: string): string {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] || `${assetTag}.liftnet-provision`;
}

/** Master-admin source of truth for physical servers and their signed install packages. */
function EdgeApplianceInventory() {
  const [t] = useTranslation("global");
  const { userLogged, globalOrganizationId, globalOrganizationName } = useSelector(
    (state: RootState) => state.home,
  );
  // Role only, as LiftCloud's RequireMasterAdmin checks it: the legacy
  // master-organization match would show controls the server then refuses.
  const authorized = process.env.REACT_APP_ENVIRONMENT !== "OFFLINE" && isMasterAdmin(userLogged);
  const [open, setOpen] = useState(false);
  const [busyID, setBusyID] = useState("");
  const [options, setOptions] = useState<DiskOption[]>([]);
  const [form] = Form.useForm<CreateEdgeAppliance>();

  const { data, error, isLoading } = useGetEdgeAppliancesQuery(undefined, { skip: !authorized });
  const [createAppliance, createState] = useCreateEdgeApplianceMutation();
  const [deleteAppliance] = useDeleteEdgeApplianceMutation();
  const [issuePackage, { reset: releasePackage }] = useIssueEdgeAppliancePackageMutation();
  const [getFactoryImage] = useLazyGetLatestFactoryImageQuery();
  const [factoryBusy, setFactoryBusy] = useState<FactoryDownload | null>(null);

  const downloadFactoryMedia = async (kind: FactoryDownload) => {
    setFactoryBusy(kind);
    try {
      const { data: image } = await getFactoryImage().unwrap();
      const { url, sha256 } = factoryDownload[kind].pick(image);
      if (!url) {
        message.info(t("edgeInventory.noToolsYet"));
        return;
      }
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Stays open: operators copy the digest to verify the download (certutil -hashfile).
      notification.success({
        message: t(factoryDownload[kind].success, { image: image.image_id }),
        description: (
          <>
            {t("edgeInventory.sha256Label")}{" "}
            <Typography.Text copyable code>
              {sha256}
            </Typography.Text>
          </>
        ),
        duration: 0,
      });
    } catch (requestError) {
      if (
        typeof requestError === "object" &&
        requestError !== null &&
        "status" in requestError &&
        requestError.status === 404
      ) {
        message.info(t("edgeInventory.noImageYet"));
      } else {
        message.error(apiErrorMessage(requestError, t("edgeInventory.downloadImageError")));
      }
    } finally {
      setFactoryBusy(null);
    }
  };

  const importDiscovery = async (file: File) => {
    try {
      const discovery = JSON.parse(await file.text()) as HardwareDiscovery;
      if (
        discovery.schema !== 1 ||
        discovery.compatible !== compatible ||
        !Array.isArray(discovery.disks) ||
        typeof discovery.checks !== "object" ||
        discovery.checks === null ||
        Object.values(discovery.checks).some((passed) => typeof passed !== "boolean") ||
        hardwareChecks.some((check) => typeof discovery.checks[check] !== "boolean")
      ) {
        throw new Error(t("edgeInventory.discoveryInvalid"));
      }
      const failedChecks = Object.entries(discovery.checks)
        .filter(([, passed]) => !passed)
        .map(([name]) => name);
      if (failedChecks.length) {
        throw new Error(
          t("edgeInventory.discoveryIncompatible", { checks: failedChecks.join(", ") }),
        );
      }
      const detected = diskOptions(discovery.disks);
      if (!detected.length) throw new Error(discovery.error || t("edgeInventory.discoveryNoDisk"));
      setOptions(detected);
      const values: Partial<CreateEdgeAppliance> = {};
      if (discovery.chassis_serial) values.chassis_serial = discovery.chassis_serial;
      if (discovery.disks.length === 1) {
        values.target_disk = preferredDiskID(discovery.disks[0].by_id);
      }
      form.setFieldsValue(values);
      message.success(t("edgeInventory.discoveryLoaded", { count: discovery.disks.length }));
    } catch (importError) {
      message.error(
        importError instanceof Error ? importError.message : t("edgeInventory.discoveryInvalid"),
      );
    }
    return false;
  };

  const create = async (values: CreateEdgeAppliance) => {
    try {
      await createAppliance({
        ...values,
        organization_id: globalOrganizationId,
        chassis_serial: values.chassis_serial?.trim() || undefined,
      }).unwrap();
      setOpen(false);
      form.resetFields();
      setOptions([]);
      message.success(t("edgeInventory.createSuccess"));
    } catch (requestError) {
      message.error(apiErrorMessage(requestError, t("edgeInventory.createError")));
    }
  };

  const remove = async (row: EdgeAppliance) => {
    setBusyID(row.id);
    try {
      await deleteAppliance({ id: row.id }).unwrap();
      message.success(t("edgeInventory.deleteSuccess", { asset: row.asset_tag }));
    } catch (requestError) {
      message.error(apiErrorMessage(requestError, t("edgeInventory.deleteError")));
    } finally {
      setBusyID("");
    }
  };

  const download = async (row: EdgeAppliance) => {
    setBusyID(row.id);
    try {
      const result = await issuePackage({ id: row.id }).unwrap();
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = downloadName(result.contentDisposition, row.asset_tag);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
      message.success(t("edgeInventory.packageSuccess", { asset: row.asset_tag }));
    } catch (requestError) {
      message.error(apiErrorMessage(requestError, t("edgeInventory.packageError")));
    } finally {
      // Drop the package (it carries the disk recovery key) from the store.
      releasePackage();
      setBusyID("");
    }
  };

  const columns: ColumnsType<EdgeAppliance> = [
    { title: t("edgeInventory.assetTag"), dataIndex: "asset_tag" },
    { title: t("edgeInventory.hostname"), dataIndex: "hostname" },
    {
      title: t("edgeInventory.organization"),
      render: (_value, row) => row.organization?.name || row.organization_id,
    },
    {
      title: t("edgeInventory.targetDisk"),
      dataIndex: "target_disk",
      render: (value: string) => (
        <Typography.Text code copyable>
          {value}
        </Typography.Text>
      ),
    },
    {
      title: t("edgeInventory.chassisSerial"),
      dataIndex: "chassis_serial",
      render: (value?: string) => value || "—",
    },
    {
      title: t("edgeInventory.state"),
      dataIndex: "state",
      render: (value: EdgeAppliance["state"]) => (
        <Tag color={stateColor[value]}>{t(`edgeInventory.states.${value}`)}</Tag>
      ),
    },
    {
      title: t("edgeInventory.packageRevision"),
      dataIndex: "package_revision",
      align: "center",
    },
    {
      title: t("edgeInventory.actions"),
      key: "actions",
      render: (_value, row) => (
        <Space>
          <Popconfirm
            title={
              <Space direction="vertical">
                <Typography.Text strong>
                  {t("edgeInventory.packageConfirm", { asset: row.asset_tag })}
                </Typography.Text>
                <Typography.Text>
                  {t("edgeInventory.packageConfirmDescription", {
                    hostname: row.hostname,
                    disk: row.target_disk,
                  })}
                </Typography.Text>
              </Space>
            }
            onConfirm={() => download(row)}
          >
            <Button
              type="primary"
              loading={busyID === row.id}
              disabled={!["ready", "package_issued"].includes(row.state)}
            >
              {t("edgeInventory.downloadPackage")}
            </Button>
          </Popconfirm>
          {row.state === "ready" && (
            <Popconfirm
              title={t("edgeInventory.deleteConfirm", { asset: row.asset_tag })}
              onConfirm={() => remove(row)}
            >
              <Button danger loading={busyID === row.id}>
                {t("edgeInventory.delete")}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!authorized) return null;

  return (
    <Card
      title={t("edgeInventory.title")}
      style={{ margin: "0 12px 16px" }}
      extra={
        <Button type="primary" disabled={!globalOrganizationId} onClick={() => setOpen(true)}>
          {t("edgeInventory.add")}
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        message={t("edgeInventory.sharedImageTitle")}
        description={t("edgeInventory.sharedImageDescription")}
        style={{ marginBottom: 12 }}
        action={
          <Space direction="vertical">
            {(["image", "tools"] as const).map((kind) => (
              <Button
                key={kind}
                type="primary"
                ghost
                block
                loading={factoryBusy === kind}
                onClick={() => downloadFactoryMedia(kind)}
              >
                {t(factoryDownload[kind].label)}
              </Button>
            ))}
          </Space>
        }
      />
      {error && (
        <Alert
          type="error"
          showIcon
          message={apiErrorMessage(error, t("edgeInventory.loadError"))}
          style={{ marginBottom: 12 }}
        />
      )}
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={false}
        scroll={{ x: 1100 }}
        locale={{
          emptyText: (
            <Empty description={t("edgeInventory.empty")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ),
        }}
      />
      <Modal
        open={open}
        title={t("edgeInventory.addTitle")}
        okText={t("edgeInventory.create")}
        cancelText={t("edgeInventory.cancel")}
        confirmLoading={createState.isLoading}
        onOk={() => form.submit()}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
          setOptions([]);
        }}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          message={t("edgeInventory.selectedOrganization", {
            organization: globalOrganizationName || globalOrganizationId,
          })}
          style={{ marginBottom: 16 }}
        />
        <Upload
          accept=".json,application/json"
          beforeUpload={importDiscovery}
          showUploadList={false}
        >
          <Button>{t("edgeInventory.importDiscovery")}</Button>
        </Upload>
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          {t("edgeInventory.discoveryHelp")}
        </Typography.Paragraph>
        <Form form={form} layout="vertical" onFinish={create} preserve={false}>
          <Form.Item
            name="asset_tag"
            label={t("edgeInventory.assetTag")}
            rules={[{ required: true }, { pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/ }]}
          >
            <Input autoComplete="off" placeholder="EDGE-023" />
          </Form.Item>
          <Form.Item
            name="hostname"
            label={t("edgeInventory.hostname")}
            normalize={(value: string) => value.trim().toLowerCase()}
            rules={[{ required: true }, { pattern: /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/ }]}
          >
            <Input autoComplete="off" placeholder="edge-023" />
          </Form.Item>
          <Form.Item
            name="target_disk"
            label={t("edgeInventory.targetDisk")}
            extra={t("edgeInventory.targetDiskHelp")}
            rules={[{ required: true }, { pattern: /^[A-Za-z0-9][A-Za-z0-9._:+-]{0,254}$/ }]}
          >
            <AutoComplete options={options} placeholder="ata-MODEL_SERIAL" filterOption />
          </Form.Item>
          <Form.Item name="chassis_serial" label={t("edgeInventory.chassisSerial")}>
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default EdgeApplianceInventory;
